import { useCallback, useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useAuth } from "@/contexts/AuthContext"
import {
  createOrgUser,
  deleteOrgUser,
  getApiErrorMessage,
  listOrgUsers,
  updateOrgUser,
} from "@/lib/orgUsers"
import type { OrgUser, UserRole } from "@/types/orgUser"

export function OrgUsersManager() {
  const { user: currentUser } = useAuth()
  const [users, setUsers] = useState<OrgUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showInviteForm, setShowInviteForm] = useState(false)
  const [inviteEmail, setInviteEmail] = useState("")
  const [inviteName, setInviteName] = useState("")
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null)
  const [inviteRole, setInviteRole] = useState<UserRole>("user")
  const [submitting, setSubmitting] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState("")
  const [editRole, setEditRole] = useState<UserRole>("user")

  const loadUsers = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await listOrgUsers()
      setUsers(data)
    } catch (e) {
      setError(getApiErrorMessage(e))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (currentUser?.role === "admin") {
      loadUsers()
    }
  }, [currentUser?.role, loadUsers])

  if (currentUser?.role !== "admin") {
    return null
  }

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    setInviteSuccess(null)
    try {
      const result = await createOrgUser({
        email: inviteEmail,
        displayName: inviteName || undefined,
        role: inviteRole,
      })
      setInviteEmail("")
      setInviteName("")
      setInviteRole("user")
      setShowInviteForm(false)
      setInviteSuccess(
        `Invitation email sent to ${result.invitation.email}. They will appear here after accepting.`
      )
    } catch (err) {
      setError(getApiErrorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  function startEdit(u: OrgUser) {
    setEditingId(u.userId)
    setEditName(u.displayName ?? "")
    setEditRole(u.role)
  }

  async function saveEdit(userId: string) {
    setSubmitting(true)
    setError(null)
    try {
      await updateOrgUser(userId, {
        displayName: editName || undefined,
        role: editRole,
      })
      setEditingId(null)
      await loadUsers()
    } catch (err) {
      setError(getApiErrorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleDelete(userId: string, email: string) {
    if (!window.confirm(`Remove ${email} from your organisation?`)) return
    setError(null)
    try {
      await deleteOrgUser(userId)
      await loadUsers()
    } catch (err) {
      setError(getApiErrorMessage(err))
    }
  }

  return (
    <div className="space-y-3 px-2 py-1">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          {currentUser.orgName
            ? `Organisation: ${currentUser.orgName}`
            : "Organisation members"}
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={() => {
            setShowInviteForm((v) => !v)
            if (showInviteForm) setInviteSuccess(null)
          }}
        >
          {showInviteForm ? "Cancel" : "Invite user"}
        </Button>
      </div>

      {error && (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      )}

      {inviteSuccess && (
        <p className="text-xs text-muted-foreground" role="status">
          {inviteSuccess}
        </p>
      )}

      {showInviteForm && (
        <form onSubmit={handleInvite} className="space-y-2 rounded-md border p-2">
          <Input
            type="email"
            placeholder="Email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            required
            className="h-8 text-xs"
          />
          <Input
            type="text"
            placeholder="Name (optional)"
            value={inviteName}
            onChange={(e) => setInviteName(e.target.value)}
            className="h-8 text-xs"
          />
          <label className="flex items-center gap-2 text-xs">
            <span>Role</span>
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as UserRole)}
              className="h-8 flex-1 rounded-md border bg-background px-2 text-xs"
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </label>
          <Button
            type="submit"
            size="sm"
            className="w-full h-8 text-xs"
            disabled={submitting}
          >
            {submitting ? "Inviting…" : "Send invite"}
          </Button>
        </form>
      )}

      {loading ? (
        <p className="text-xs text-muted-foreground">Loading members…</p>
      ) : users.length === 0 ? (
        <p className="text-xs text-muted-foreground">No members yet.</p>
      ) : (
        <ul className="space-y-2">
          {users.map((u) => (
            <li
              key={u.userId}
              className="rounded-md border p-2 text-xs space-y-2"
            >
              {editingId === u.userId ? (
                <>
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="Display name"
                    className="h-8 text-xs"
                  />
                  <select
                    value={editRole}
                    onChange={(e) => setEditRole(e.target.value as UserRole)}
                    className="h-8 w-full rounded-md border bg-background px-2 text-xs"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      size="sm"
                      className="h-7 flex-1 text-xs"
                      disabled={submitting}
                      onClick={() => saveEdit(u.userId)}
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 flex-1 text-xs"
                      onClick={() => setEditingId(null)}
                    >
                      Cancel
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <p className="font-medium truncate">{u.email}</p>
                    <p className="text-muted-foreground">
                      {u.displayName || "—"} · {u.role}
                      {u.userId === currentUser.id ? " (you)" : ""}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 flex-1 text-xs"
                      onClick={() => startEdit(u)}
                    >
                      Edit
                    </Button>
                    {u.userId !== currentUser.id && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 flex-1 text-xs text-destructive hover:text-destructive"
                        onClick={() => handleDelete(u.userId, u.email)}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}